import {
  RoutineWorkflow,
  useRoutineAlerts,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AlertSummary,
  Button,
  Colors,
  Icon,
  LoadingIndicator,
  SlideAction,
  SlideLayout,
  type ColumnData,
  type SlideLayoutLocationState,
  tokens,
  ViewMode,
} from '@gripp/shared-ui';
import { EditAlertForm } from 'libs/shared-ui/src/components/new-alerts/components/editAlertForm';
import { NewAlertForm } from 'libs/shared-ui/src/components/new-alerts/components/newAlertForm';
import { MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NewAlertHeaderAction } from '../../shared/components/alerts/newAlertHeaderAction';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';

type FormRef = {
  handleSubmit: (fn: (data: unknown) => Promise<void>) => () => void;
  reset: () => void;
} | null;

type AlertFormLocationState = SlideLayoutLocationState & {
  fromRoutine?: boolean;
} & Record<string, unknown>;

const toAlertFormLocationState = (
  state: unknown
): AlertFormLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as AlertFormLocationState)
    : null;

// ─── Alert list (left column) ────────────────────────────────────────────────

type AlertListContentProps = {
  assetId: string;
  routineId: string;
};

const AlertListContent = ({ assetId, routineId }: AlertListContentProps) => {
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { routineWorkflows, isLoading } = useRoutineAlerts(
    workspaceAsset?.id ?? '',
    routineId
  );

  const handleAlertSelected = (selectedAlert: RoutineWorkflow) => {
    if (!selectedAlert?.id) {
      return;
    }

    navigate(
      `${basePath}/routines/${routineId}/alerts/${selectedAlert.id}/edit`
    );
  };

  if (isLoading || !workspaceAsset) {
    return <LoadingIndicator flexContainer />;
  }

  return (
    <View style={listStyles.container}>
      <ScrollView>
        {routineWorkflows?.map((item: RoutineWorkflow, index: number) => (
          <AlertSummary
            key={`${index}-${item.id}`}
            workflow={item}
            workspaceAsset={workspaceAsset}
            routineId={routineId}
            onAlertSelected={handleAlertSelected}
          />
        ))}
      </ScrollView>
    </View>
  );
};

// ─── Form header (right column headerComponent) ───────────────────────────────

type AlertFormHeaderProps = {
  formRef: MutableRefObject<FormRef>;
};

const AlertFormHeader = ({ formRef }: AlertFormHeaderProps) => (
  <Pressable
    onPress={() => formRef.current?.reset()}
    style={formStyles.resetButton}
  >
    <Icon name="Reset" size={20} color={Colors.black} />
  </Pressable>
);

// ─── Form content (right column) ─────────────────────────────────────────────

type AlertFormContentProps = {
  assetId: string;
  routineId: string;
  alertId?: string;
  onBack: () => void;
  formRef: MutableRefObject<FormRef>;
  onRegisterBackGuard: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
};

const AlertFormContent = ({
  assetId,
  routineId,
  alertId,
  onBack,
  formRef,
  onRegisterBackGuard,
}: AlertFormContentProps) => {
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { t } = useTranslation();
  const isEdit = !!alertId;

  if (!workspaceAsset) return null;

  const confirmBottomComponent = (onSubmit: () => void, disabled: boolean) => (
    <View>
      <Button
        variant="rectangular"
        size="large"
        state={disabled ? 'default' : 'active'}
        disabled={disabled}
        onPress={() => {
          if (!disabled) {
            onSubmit();
          }
        }}
        style={formStyles.confirmButton}
      >
        {t('alerts.confirm')}
      </Button>
    </View>
  );

  return (
    <View style={formStyles.container}>
      {isEdit ? (
        <EditAlertForm
          key={alertId}
          alertId={alertId}
          routineId={routineId}
          workspaceAssetId={workspaceAsset.id}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      ) : (
        <NewAlertForm
          routineId={routineId}
          workspaceAssetId={workspaceAsset.id}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      )}
    </View>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AssetAlertFormPage = () => {
  const { assetId, routineId, alertId, issueId } = useParams<{
    assetId: string;
    routineId: string;
    alertId?: string;
    issueId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<FormRef>(null);
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!assetId || !routineId) return null;

  const basePath = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;
  const routineUrl = `${basePath}/routines/${routineId}`;
  const locationState = toAlertFormLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  // Set by AssetRoutineSubmitHeader when the routine has no alerts yet —
  // signals that the actual previous step is the routine submit page, not
  // the (empty) alerts list, so the left column should mirror that.
  const fromRoutine = locationState?.fromRoutine;
  // Preserve the rest of the state (e.g. RoutineSubmitSource `from`) when
  // going back so AssetRoutineSubmit doesn't fall back to its AssetCard
  // default. `fromRoutine` itself is dropped — it only applies to this hop.
  const backState = (() => {
    if (!locationState) return undefined;
    const {
      fromRoutine: _dropFromRoutine,
      slideLayoutInitialTransition: _dropInitialTransition,
      ...rest
    } = locationState;
    return Object.keys(rest).length > 0 ? rest : undefined;
  })();
  const getBackStateWithTransition = (transition: SlideAction) => ({
    ...(backState ?? {}),
    slideLayoutInitialTransition: transition,
  });

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn: ColumnData = fromRoutine
    ? {
        title: 'routines.editRoutines.editRoutinesButton',
        mainComponent: <AssetRoutineSubmitColumn assetId={assetId} />,
        headerComponent: (
          <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
        ),
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getBackStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      }
    : {
        title: 'alerts.alerts',
        mainComponent: (
          <AlertListContent assetId={assetId} routineId={routineId} />
        ),
        headerComponent: <NewAlertHeaderAction />,
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getBackStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      };

  const rightColumn: ColumnData = {
    title: alertId ? 'alerts.editAlert' : 'alerts.newAlert',
    mainComponent: (
      <AlertFormContent
        assetId={assetId}
        routineId={routineId}
        alertId={alertId}
        onBack={() => navigate(routineUrl, { state: backState })}
        formRef={formRef}
        onRegisterBackGuard={(guard) => {
          backGuardRef.current = guard;
        }}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonGuard,
    // When the user came directly from the routine page (no alerts yet),
    // back skips the empty alerts list and returns to the routine.
    onBackButtonPress: () =>
      navigate(fromRoutine ? routineUrl : `${routineUrl}/alerts`, {
        state: getBackStateWithTransition(SlideAction.PREV),
      }),
    headerComponent: <AlertFormHeader formRef={formRef} />,
    disableScroll: true,
  };

  return (
    <AssetRouteProvider
      value={{
        basePath,
        listPath: issueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId: issueId ?? undefined,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const listStyles = StyleSheet.create({
  container: {
    flex: 1,
    margin: tokens.spacing.xl,
  },
});

const formStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  resetButton: {
    padding: 4,
  },
  confirmButton: {
    width: '100%',
  },
});
